import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dashboardService } from "../../services/api";
import StatCard from "../../components/StatCard/StatCard";
import { fmtDateTimezone } from "../utils/dateUtils/fmtDate";
import { fmtCurrencyBR } from "../../utils/formatBrl";
import { BadgeCheck, Banknote, ChevronLeft, ChevronRight, Clock, LineChart, Receipt } from "lucide-react";
import useUrlFilters from "./useUrlFilters";
import FilterChip from "./FilterChip";
import AdminFilters from "./AdminFilters";
import { AdminMetrics } from "./AdminMetrics";

// Filtros na URL. `bot` busca no @ do bot; `bot_id` é exato (link de Bots →
// Vendas) e `bot_label` só dá nome ao chip.
const FILTER_KEYS = ["start_date", "end_date", "username", "email", "bot", "bot_id", "bot_label"];
const SEARCHES = [
	{ key: "username", placeholder: "Username do seller" },
	{ key: "email", placeholder: "E-mail do seller" },
	{ key: "bot", placeholder: "@ do bot" },
];

// "Plano Upsell + Order Bump + Order Bump" → { base: "Upsell", bumps: 2 }.
// A categoria é gravada na venda (mesmo dado da coluna "Tipo do Plano" de Vendas).
export function planType(category) {
	if (!category) return null;
	const [head, ...extras] = category.split("+").map((part) => part.trim());
	const bumps = extras.filter((part) => /^order\s*bump$/i.test(part)).length;
	return { base: head.replace(/^Plano\s+/i, ""), bumps };
}

// Tipo da venda (Normal, Upsell, Downsell, Disparo...) e, embaixo, o plano comprado.
function PaymentPlan({ p }) {
	const type = planType(p.category_name);
	const plan = [p.plan_name, p.plan_duration].filter(Boolean).join(" · ");
	if (!type && !plan) return <>-</>;
	return (
		<div className="admin-plan-cell">
			{type && (
				<div className="admin-plan-type">
					<span>{type.base}</span>
					{type.bumps > 0 && (
						<em>{type.bumps > 1 ? `+ ${type.bumps}× Order Bump` : "+ Order Bump"}</em>
					)}
				</div>
			)}
			{plan && <div className="admin-plan-name" title={plan}>{plan}</div>}
		</div>
	);
}

// Bot que fez a venda; o clique abre esse bot na seção Bots.
function PaymentBot({ p, onOpen }) {
	if (!p.bot_username) return <>{p.bot_id ? `Bot #${p.bot_id}` : "-"}</>;
	const label = `@${p.bot_username}`;
	return (
		<button type="button" className="admin-link" title="Abrir este bot em Bots" onClick={() => onOpen(p.bot_username)}>
			{label}
		</button>
	);
}

// Valor na moeda da venda; se estrangeira, mostra também o equivalente em BRL.
function PaymentAmount({ p }) {
	const cur = (p.currency || "BRL").toUpperCase();
	if (cur === "BRL") return <>{fmtCurrencyBR.format(p.amount)}</>;
	const native = new Intl.NumberFormat("pt-BR", { style: "currency", currency: cur }).format(p.amount || 0);
	return (
		<>
			{native}
			<span style={{ color: "var(--text-secondary)", marginLeft: 6, fontSize: "0.85em" }}>
				≈ {fmtCurrencyBR.format(p.amount_brl ?? p.amount)}
			</span>
		</>
	);
}

export default function AdminPayments() {
	const [payments, setPayments] = useState([]);
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const [applied, applyFilters, filtersKey] = useUrlFilters(FILTER_KEYS);
	const [metrics, setMetrics] = useState({
		payments_revenue: 0,
		payments_generated_sum: 0,
		payments_paid_sum: 0,
		payments_approved_count: 0,
		payments_pending_count: 0,
	});
	const [pagination, setPagination] = useState({});
	const [currentPage, setCurrentPage] = useState(1);

	useEffect(() => {
		loadPayments(1);
		// eslint-disable-next-line
	}, [filtersKey]);

	const loadPayments = async (page = 1) => {
		try {
			setLoading(true);
			const { bot_label, ...params } = applied;
			const { data } = await dashboardService.getAdminPayments({
				page,
				...params,
			});
			setPayments(data.payments || []);
			setMetrics({
				payments_revenue: data.payments_revenue,
				payments_generated_sum: data.payments_generated_sum,
				payments_paid_sum: data.payments_paid_sum,
				payments_approved_count: data.payments_approved_count,
				payments_pending_count: data.payments_pending_count,
			});
			setPagination(data.pagination || {});
			setCurrentPage(page);
		} catch (err) {
			console.error(err);
		} finally {
			setLoading(false);
		}
	};

	const openBot = (username) =>
		navigate(`/dashboard/admin/bots?${new URLSearchParams({ username })}`);

	const formatStatusLabel = (status) => {
		if (!status) return "-";
		const normalized = status.toLowerCase();
		if (normalized === "approved") return "Pago";
		if (normalized === "pending") return "Pendente";
		if (normalized === "expired") return "Expirado";
		if (normalized === "failed") return "Falhou";
		return status;
	};

	const getStatusClass = (status) => {
		const normalized = (status || "").toLowerCase();
		if (normalized === "approved") return "text-green-400";
		if (normalized === "pending") return "text-yellow-400";
		if (normalized === "expired") return "text-gray-400";
		if (normalized === "failed") return "text-red-400";
		return "text-gray-300";
	};

	const getBgClass = (status) => {
		const normalized = (status || "").toLowerCase();
		if (normalized === "approved") return "bg-green-900";
		if (normalized === "pending") return "bg-yellow-900";
		if (normalized === "expired") return "bg-gray-900";
		if (normalized === "failed") return "bg-red-900";
		return "bg-gray-300";
	};

	return (
		<>
			<AdminFilters values={applied} onApply={applyFilters} period searches={SEARCHES}>
				{applied.bot_id && (
					<FilterChip
						label={`Vendas de ${applied.bot_label || `Bot #${applied.bot_id}`}`}
						onClear={() => applyFilters({ ...applied, bot_id: "", bot_label: "" })}
					/>
				)}
			</AdminFilters>

			<AdminMetrics>
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
					<StatCard title="Faturamento" value={fmtCurrencyBR.format(metrics.payments_revenue)} icon={{ Icon: LineChart, color: 'green' }} />
					{/* "Comissão": soma do split (taxa da Spectrum), não o valor das vendas. */}
					<StatCard title="Comissão gerada" value={fmtCurrencyBR.format(metrics.payments_generated_sum)} icon={{ Icon: Receipt, color: 'blue' }} />
					<StatCard title="Comissão paga" value={fmtCurrencyBR.format(metrics.payments_paid_sum)} icon={{ Icon: Banknote, color: 'green' }} />
					<StatCard title="Pagamentos Aprovados" value={metrics.payments_approved_count} icon={{ Icon: BadgeCheck, color: 'purple' }} />
					<StatCard title="Pagamentos Pendentes" value={metrics.payments_pending_count} icon={{ Icon: Clock, color: 'red' }} />
				</div>
			</AdminMetrics>

			{/* TABELA - Desktop */}
			<div className="sx-desktop mt-6">
				<table className="sx-table">
					<thead>
						<tr>
							<th>ID</th>
							<th>SELLER</th>
							<th>Bot</th>
							<th>Plano</th>
							<th>Valor</th>
                            <th>Split</th>
							<th>Status</th>
							<th>Data</th>
						</tr>
					</thead>
					<tbody>
						{payments.map((p) => (
							<tr key={p.id}>
								<td>{p.id}</td>
								<td>{p.user_id || '-'} - {p.username}</td>
								<td><PaymentBot p={p} onOpen={openBot} /></td>
								<td><PaymentPlan p={p} /></td>
								<td><PaymentAmount p={p} /></td>
								<td>{fmtCurrencyBR.format(p.split)}</td>
								<td className={getStatusClass(p.status)}>
									<div className={getBgClass(p.status) + " font-bold inline-block px-2 py-1 rounded text-xs font-semibold"}>
										{formatStatusLabel(p.status)}
									</div>
								</td>
								<td>{fmtDateTimezone(p.created_at)}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* CARDS - Mobile */}
			<div className="sx-mobile mt-6">
				{payments.map((p) => (
					<div key={p.id} className="mobile-card">
						<div className="sx-card-header">
							<span className="sx-card-title">{p.username}</span>
							<span className={getBgClass(p.status) + " font-bold inline-block px-2 py-1 rounded text-xs font-semibold " + getStatusClass(p.status)}>
								{formatStatusLabel(p.status)}
							</span>
						</div>
						<div className="sx-card-row">
							<span style={{ color: "var(--text-secondary)" }}>ID: </span>
							{p.id} · User {p.user_id || '-'}
						</div>
						<div className="sx-card-row">
							<span style={{ color: "var(--text-secondary)" }}>Bot: </span>
							<PaymentBot p={p} onOpen={openBot} />
						</div>
						<div className="sx-card-row">
							<span style={{ color: "var(--text-secondary)" }}>Plano: </span>
							<PaymentPlan p={p} />
						</div>
						<div className="sx-card-row">
							<span style={{ color: "var(--text-secondary)" }}>Valor: </span>
							<strong><PaymentAmount p={p} /></strong>
							<span style={{ color: "var(--text-secondary)", marginLeft: 8 }}>Split: </span>
							<strong>{fmtCurrencyBR.format(p.split)}</strong>
						</div>
						<div className="sx-card-row">
							<span style={{ color: "var(--text-secondary)" }}>Data: </span>
							{fmtDateTimezone(p.created_at)}
						</div>
					</div>
				))}
				{payments.length === 0 && (
					<div className="empty-state">
						<Banknote size={28} />
						<h4>Nenhum pagamento encontrado</h4>
					</div>
				)}
			</div>

			{/* PAGINAÇÃO */}
			{!loading && payments.length > 0 && (
				<div className="mt-8 flex items-center justify-between">
					<div className="text-sm text-gray-600">
						Página {currentPage} de {pagination.pages} (
						{pagination.total} pagamentos)
					</div>
					<div className="flex gap-2">
						<button
							onClick={() => loadPayments(currentPage - 1)}
							disabled={!pagination.has_prev}
							className="btn-secondary inline-flex items-center gap-1"
						>
							<ChevronLeft size={16} /> Anterior
						</button>
						<button
							onClick={() => loadPayments(currentPage + 1)}
							disabled={!pagination.has_next}
							className="btn-primary inline-flex items-center gap-1"
						>
							Próximo <ChevronRight size={16} />
						</button>
					</div>
				</div>
			)}
		</>
	);
}
